import React, { FC } from 'react'
import Paper from '@mui/material/Paper'
import format from 'date-fns/format'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { OuterLink, TableToolbar } from 'src/components/ui'
import { Sensor, SensorCurrentData } from 'src/api'
import { ReadingType } from 'src/enums'

const columns: GridColDef<Reading>[] = [
  { field: 'name', headerName: 'Reading', flex: 1, sortable: false, disableColumnMenu: true },
  {
    field: 'value',
    headerName: 'Value',
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }) => {
      const isLocation = row.type === ReadingType.LocationCellular || row.type === ReadingType.LocationGps
      if (isLocation && row.value) {
        return <OuterLink to={encodeURI(`https://www.google.com/maps/search/?api=1&query=${row.value}`)}>{row.value}</OuterLink>
      }
      return row.value ?? ''
    }
  },
  {
    field: 'lastPing',
    headerName: 'Last Ping',
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    valueFormatter: (params) => (params.value ? format(new Date(params.value as string), 'PPpp') : '')
  }
]
interface SensorLastReadingsProps {
  sensor: Sensor
}
export interface Reading {
  name: string
  value: string | number | undefined
  lastPing: string | undefined
  type: ReadingType
}
type GetReadingRow = (data: SensorCurrentData) => Reading
interface ReadingRows {
  accelerationRms: GetReadingRow
  accelerationX: GetReadingRow
  accelerationY: GetReadingRow
  accelerationZ: GetReadingRow
  battery: GetReadingRow
  temperature: GetReadingRow
  heatIndex: GetReadingRow
  humidity: GetReadingRow
  locationCellular: GetReadingRow
  locationGps: GetReadingRow
  light: GetReadingRow
  pressure: GetReadingRow
  dewpoint: GetReadingRow
  signalStrength: GetReadingRow
  orientationMotionX: GetReadingRow
  orientationMotionY: GetReadingRow
  orientationMotionZ: GetReadingRow
  orientationTiltZ: GetReadingRow
  firmwareVersion: GetReadingRow
  configurationTime: GetReadingRow
}

const rows: ReadingRows = {
  accelerationRms: (data) => {
    return {
      name: 'Acceleration RMS',
      value: data.accelerationRms,
      lastPing: data.accelerationTime,
      type: ReadingType.Acceleration
    }
  },
  accelerationX: (data) => {
    return {
      name: 'Acceleration X',
      value: data.accelerationX,
      lastPing: data.accelerationTime,
      type: ReadingType.Acceleration
    }
  },
  accelerationY: (data) => {
    return {
      name: 'Acceleration Y',
      value: data.accelerationY,
      lastPing: data.accelerationTime,
      type: ReadingType.Acceleration
    }
  },
  accelerationZ: (data) => {
    return {
      name: 'Acceleration Z',
      value: data.accelerationZ,
      lastPing: data.accelerationTime,
      type: ReadingType.Acceleration
    }
  },
  battery: (data) => {
    return {
      name: 'Battery',
      value: data.batteryLevel ? `${data.batteryLevel}%` : '',
      lastPing: data.batteryLevelTime,
      type: ReadingType.Battery
    }
  },
  dewpoint: (data) => {
    return {
      name: 'Dew Point',
      value: data.dewPointF ? `${data.dewPointF}°F` : '',
      lastPing: data.dewPointTime,
      type: ReadingType.Dewpoint
    }
  },
  humidity: (data) => {
    return {
      name: 'Humidity',
      value: data.humidity ? `${data.humidity}%` : '',
      lastPing: data.humidityTime,
      type: ReadingType.Humidity
    }
  },
  light: (data) => {
    return {
      name: 'Light',
      value: data.light,
      lastPing: data.lightTime,
      type: ReadingType.Light
    }
  },
  locationCellular: (data) => {
    return {
      name: 'Location Cellular',
      value: data.latitudeCellural && data.longitudeCellural ? `${data.latitudeCellural}, ${data.longitudeCellural}` : '',
      lastPing: data.locationUpdatedAt,
      type: ReadingType.LocationCellular
    }
  },
  locationGps: (data) => {
    return {
      name: 'Location GPS',
      value: data.latitudeGps && data.longitudeGps ? `${data.latitudeGps}, ${data.longitudeGps}` : '',
      lastPing: data.locationGpsUpdatedAt,
      type: ReadingType.LocationGps
    }
  },
  orientationMotionX: (data) => {
    return {
      name: 'Orientation Motion X',
      value: data.orientationMotionX,
      lastPing: data.orientationTime,
      type: ReadingType.Orientation
    }
  },
  orientationMotionY: (data) => {
    return {
      name: 'Orientation Motion Y',
      value: data.orientationMotionY,
      lastPing: data.orientationTime,
      type: ReadingType.Orientation
    }
  },
  orientationMotionZ: (data) => {
    return {
      name: 'Orientation Motion Z',
      value: data.orientationMotionZ,
      lastPing: data.orientationTime,
      type: ReadingType.Orientation
    }
  },
  orientationTiltZ: (data) => {
    return {
      name: 'Orientation Tilt Z',
      value: data.orientationTiltZ,
      lastPing: data.orientationTime,
      type: ReadingType.Orientation
    }
  },
  pressure: (data) => {
    return {
      name: 'Pressure',
      value: data.pressure,
      lastPing: data.pressureTime,
      type: ReadingType.Pressure
    }
  },
  signalStrength: (data) => {
    return {
      name: 'Signal Strength',
      value: data.signalStrength,
      lastPing: data.signalStrengthTime,
      type: ReadingType.SignalStrength
    }
  },
  temperature: (data) => {
    return {
      name: 'Temperature',
      value: data.temperatureF ? `${data.temperatureF}°F` : '',
      lastPing: data.temperatureTime,
      type: ReadingType.Temperature
    }
  },
  heatIndex: (data) => {
    return {
      name: 'Heat Index',
      value: data.heatIndexF ? `${data.heatIndexF}°F` : '',
      lastPing: data.heatIndexTime,
      type: ReadingType.HeatIndex
    }
  },
  firmwareVersion: (data) => {
    return {
      name: 'Firmware version',
      value: data.firmwareVersion || '',
      lastPing: data.firmwareVersionTime,
      type: ReadingType.Diagnostic
    }
  },
  configurationTime: (data) => ({
    name: 'Last received configuration',
    value: '-',
    lastPing: data.configurationTime,
    type: ReadingType.Configuration
  })
}

export const SensorLastReadings: FC<SensorLastReadingsProps> = (props) => {
  const { sensor } = props
  if (!sensor) return null

  const data: Reading[] = Object.values(rows).map((value: GetReadingRow) => value((sensor.currentData as SensorCurrentData) || {}))

  return (
    <Paper sx={{ height: '700px' }}>
      <DataGridPremium<Reading>
        pagination={false}
        autoHeight={false}
        disableRowSelectionOnClick
        rows={data}
        columns={columns}
        rowCount={data.length}
        getRowId={(row) => row.name}
        rowThreshold={2}
        columnThreshold={2}
        hideFooterPagination
        localeText={{
          columnMenuSortAsc: 'Sort A-Z',
          columnMenuSortDesc: 'Sort Z-A'
        }}
        slots={{
          toolbar: () => <TableToolbar exportedFileName={`${document.title}_last_readings`} />
        }}
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaderTitleContainerContent': {
            paddingLeft: '5px',
            overflow: 'visible'
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
            lineHeight: 1,
            fontSize: '13px',
            fontWeight: 400,
            color: 'rgba(0, 0, 0, 0.6)',
            overflow: 'visible'
          }
        }}
      />
    </Paper>
  )
}
